import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { formatDate, formatDateNoTime, formatPhoneNumber } from "@sp-crm/core";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { ConversationFeedBasicsFragment, SmsMessageStatus } from "generated/graphql";
import React, { useCallback, useState } from "react";
import { pluralize } from "util/pluralize";
import { ContactSearchHitsLink } from "./contact-search-hits-link";
import {
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    ReceivedStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";

interface SmsMessageThreadProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    initialThreadId?: string | null;
}

interface SmsStatusMessageProps {
    message: ConversationFeedBasicsFragment["threads"][number]["smsThread"]["messages"][number];
    mode: "icon" | "icon-and-text";
}
const SmsStatusMessage: React.FC<SmsStatusMessageProps> = props => {
    const { message, mode } = props;

    switch (message.status) {
        case SmsMessageStatus.Delivered:
            return <DeliveredStatusMessage mode={mode} />;
        case SmsMessageStatus.Failed:
            return <NotDeliveredStatusMessage mode={mode} />;
        case SmsMessageStatus.Sent:
            return <SentStatusMessage mode={mode} />;
        case SmsMessageStatus.Received:
            return <ReceivedStatusMessage mode={mode} />;
        case SmsMessageStatus.Undelivered:
            return <NotDeliveredStatusMessage mode={mode} />;
        case SmsMessageStatus.Accepted:
            return <SendingStatusMessage mode={mode} />;
        case SmsMessageStatus.Queued:
            return <SendingStatusMessage mode={mode} />;
        case SmsMessageStatus.Scheduled:
            return <SendingStatusMessage mode={mode} />;
        case SmsMessageStatus.Unknown:
            return null;
        default: {
            const exhaustiveCheck: never = message.status;
            console.error(`Unknown SMS message status: ${exhaustiveCheck}`);
            return null;
        }
    }
};

export const SmsMessageThread: React.FC<SmsMessageThreadProps> = props => {
    const { thread, initialThreadId } = props;
    const messages = thread.smsThread?.messages || [];
    const latestMessage = messages[0];
    const previousMessages = messages.slice(1);
    const isInbound = latestMessage.direction === "Inbound";
    const PREVIOUS_MESSAGES_THRESHOLD = 3;

    const [viewing, setViewing] = useState(initialThreadId === thread.id);

    const handleView = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setViewing(true);
    }, []);

    const handleDismiss = useCallback(() => {
        setViewing(false);
    }, []);

    return (
        <li key={thread.id} className="mb-4">
            <div
                className="bg-white rounded-lg shadow p-4 transition-colors hover:bg-brand-50 cursor-pointer"
                onClick={handleView}>
                <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                        <ChatBubbleLeftIcon
                            className={`h-5 w-5 text-gray-500 mt-2 ${
                                isInbound ? "transform scale-x-[-1]" : ""
                            }`}
                            aria-hidden="true"
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                            <div className="flex flex-col space-y-1 min-w-0 flex-1">
                                <div className="flex flex-col md:flex-row md:items-center gap-2 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium text-gray-900 break-words">
                                            {isInbound ? "From" : "To"}{" "}
                                            {formatPhoneNumber(
                                                isInbound
                                                    ? latestMessage.from
                                                    : latestMessage.to,
                                            )}
                                        </div>
                                    </div>
                                    <ContactSearchHitsLink
                                        hits={thread.contactSearchHits}
                                    />
                                </div>
                                <div className="flex flex-col md:flex-row md:items-center gap-2 min-w-0">
                                    <div className="text-sm text-gray-700 break-words">
                                        {latestMessage.body}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500 flex-shrink-0 md:ml-4 mt-2 md:mt-0">
                                <span>{formatDate(latestMessage.dateAdded)}</span>
                                <SmsStatusMessage message={latestMessage} mode="icon" />
                            </div>
                        </div>
                        {previousMessages.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                                <div className="space-y-1.5">
                                    {previousMessages
                                        .slice(0, PREVIOUS_MESSAGES_THRESHOLD)
                                        .map((message, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between text-xs text-gray-600 py-1 pr-2 rounded border-b border-gray-50 last:border-b-0">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="truncate">
                                                        {message.body}
                                                    </span>
                                                </div>
                                                <span className="flex-shrink-0 text-gray-400 ml-2">
                                                    {formatDateNoTime(message.dateAdded)}
                                                </span>
                                            </div>
                                        ))}
                                    {previousMessages.length >
                                        PREVIOUS_MESSAGES_THRESHOLD && (
                                        <div className="text-xs text-gray-500 italic py-1 px-2">
                                            +
                                            {previousMessages.length -
                                                PREVIOUS_MESSAGES_THRESHOLD}{" "}
                                            more{" "}
                                            {pluralize(
                                                previousMessages.length -
                                                    PREVIOUS_MESSAGES_THRESHOLD,
                                                "message",
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SendSmsPanel
                isOpen={viewing}
                onDismiss={handleDismiss}
                recipient={isInbound ? latestMessage.from : latestMessage.to}
            />
        </li>
    );
};
